import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    // Check dismissed state
    const dismissed = sessionStorage.getItem('cc_install_dismissed');
    if (dismissed) return;

    // iOS detection
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(isiOS);

    if (isiOS) {
      // Show iOS install instructions after 3 seconds
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }

    // Android/Desktop — listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  async function handleInstall() {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShowBanner(false);
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    }
  }

  function handleDismiss() {
    setShowBanner(false);
    sessionStorage.setItem('cc_install_dismissed', '1');
  }

  if (isInstalled || !showBanner) return null;

  return (
    <div
      className="fixed bottom-20 left-4 right-4 z-50 max-w-lg mx-auto bg-[#0B132B] border border-[#2563EB]/40 rounded-2xl p-4 shadow-xl shadow-black/40"
      data-testid="install-prompt"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-[#475569] hover:text-white transition-colors"
        data-testid="install-dismiss-btn"
      >
        <X size={16} />
      </button>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#2563EB]/15 flex items-center justify-center flex-shrink-0">
          <Download size={22} className="text-[#2563EB]" />
        </div>
        <div className="flex-1">
          <p className="font-['Barlow_Condensed'] text-sm font-bold tracking-tight">
            INSTALL CONNECTED COACHING
          </p>
          {isIOS ? (
            <p className="text-[11px] text-[#94A3B8] mt-0.5 leading-tight">
              Tap <span className="text-white font-semibold">Share</span> then <span className="text-white font-semibold">"Add to Home Screen"</span>
            </p>
          ) : (
            <p className="text-[11px] text-[#94A3B8] mt-0.5">
              Add to your home screen for the full experience
            </p>
          )}
        </div>
        {!isIOS && (
          <button
            onClick={handleInstall}
            className="bg-[#2563EB] hover:bg-[#3B82F6] text-white text-xs font-bold uppercase tracking-wider rounded-lg px-4 py-2.5 transition-colors"
            data-testid="install-btn"
          >
            Install
          </button>
        )}
      </div>
    </div>
  );
}
